import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../../../contexts/ToastContext';
import { window } from '../../../../../../../globals';
import { useToggle } from '../../../../../../../hooks';
import * as ReportGenerationStore from '../../../../../../../redux/reportGeneration';
import { FormLabelVariant, LabeledTextArea } from '../../../../../../generic';
import Button, { BUTTON_SIZE } from '../../../../../../generic/Button';
import Markdown from '../../../../../../generic/Markdown';
import Tabs, { useTabs } from '../../../../../../generic/Tabs';
import Text from '../../../../../../generic/Text';
import LogoUpload from '../../../../../../LogoUpload';
import type { ReportLogoAction } from '../../types';

import MakeDefaultToggle from './components/MakeDefaultToggle';
import PropagateHeaderChangesModal from './components/PropagateHeaderChangesModal';
import type { OnSave } from './types';

import styles from './Settings.module.less';

const tabIds = ['Edit', 'Preview'];
const defaultImageUrl = '/img/fossa-primary-logo.svg';
const organizationImageUrl = '/organization/logo';

type BadgeType = (typeof tabIds)[number];

type Props = {
  onSave: OnSave;
};

const Panel: VoidFunctionComponent<Props> = ({ onSave }) => {
  const addToast = useAddToast();
  const [shouldShowPropagateHeaderChangesModal, setShouldShowPropagateHeaderChangesModal] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');
  const [shouldSetDefaultState, toggleShouldSetDefaultState] = useToggle();
  const [reportLogoAction, setReportLogoAction] = useState<ReportLogoAction | null>(null);
  const [imageUrl, setImageUrl] = useState<string>(
    window.user?.organization?.image ? organizationImageUrl : defaultImageUrl,
  );
  const [isImageDisplayFailed, setIsImageDisplayFailed] = useState(false);
  const customText = useSelector(ReportGenerationStore.selectors.getCustomHeaderText);
  useEffect(() => {
    setDescription(customText);
  }, [customText]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const handleSelectImage = (file: File) => {
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      setIsImageDisplayFailed(false);
      setImageUrl(fileUrl);
      setImageFile(file);
      setReportLogoAction('SAVE');
    }
  };
  const handleRemoveImage = () => {
    setIsImageDisplayFailed(false);
    setImageUrl(defaultImageUrl);
    setImageFile(null);
    setReportLogoAction('REMOVE');
  };
  const { tabs, activeTab } = useTabs<BadgeType>({
    tabIds: description ? tabIds : ['Edit'], // Only show the Preview tab if there is a description
    defaultTab: 'Edit',
  });

  return (
    <div className={cx('panel', styles.panel)}>
      <div className={styles.panelHeader}>
        <Text.H1>Report settings</Text.H1>
        <div className={styles.actions}>
          <MakeDefaultToggle onToggle={toggleShouldSetDefaultState} />
          <Button
            onClick={async () => {
              if (isImageDisplayFailed) {
                addToast({
                  type: 'error',
                  content: 'Logo cannot be saved.',
                });
                return;
              }

              if (shouldSetDefaultState) {
                setShouldShowPropagateHeaderChangesModal(true);
              } else {
                await onSave({
                  file: imageFile,
                  reportLogoAction,
                  customText: description,
                  shouldPropagate: false,
                  shouldSetDefault: false,
                });
              }
            }}
            size={BUTTON_SIZE.LARGE}
          >
            Save settings
          </Button>
        </div>
      </div>
      <LogoUpload
        fileUrl={imageUrl}
        setFile={handleSelectImage}
        unsetFile={handleRemoveImage}
        handleImgError={() => {
          setIsImageDisplayFailed(true);
        }}
      />
      <div className={styles.description}>
        <Text.H3>Description</Text.H3>
        <Tabs>
          {tabs.map((tab) => (
            <Tabs.Tab
              key={tab.label}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...tab}
              onSelect={() => {
                tab.onSelect();
              }}
            />
          ))}
        </Tabs>
        {activeTab === 'Edit' && (
          <LabeledTextArea
            label="Uses markdown format"
            labelVariant={FormLabelVariant.Filter}
            placeholder="**Markdown text** *is* supported."
            onChange={setDescription}
            value={description}
          />
        )}
        {activeTab === 'Preview' && <Markdown text={description} />}
      </div>
      <PropagateHeaderChangesModal
        shouldShowModal={shouldShowPropagateHeaderChangesModal}
        onSave={async (shouldSetDefault, shouldPropagate) => {
          await onSave({
            file: imageFile,
            reportLogoAction,
            customText: description,
            shouldPropagate,
            shouldSetDefault,
          });
        }}
        onHide={() => {
          setShouldShowPropagateHeaderChangesModal(false);
        }}
      />
    </div>
  );
};

export default Panel;
